import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getMobileContext } from "@/utils/deviceDetection";

const WEB_BOOKING_PATH = "/search?focus=location";

const DISMISSED_AT_STORAGE_KEY = "rakb_app_prompt_dismissed_at";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;
const MODAL_DELAY_MS = 2500;
const COOKIE_CONSENT_KEY = "rakb_cookie_consent";

interface UseAppPromptReturn {
  isModalOpen: boolean;
  isOpeningApp: boolean;
  handleOpenApp: () => Promise<void>;
  handleContinueOnWeb: () => void;
}

const isAppPromptFeatureEnabled = (): boolean => {
  const envValue = import.meta.env.VITE_ENABLE_APP_PROMPT;
  return envValue === "true";
};

const isEligibleRoute = (pathname: string): boolean => {
  if (pathname === "/") {
    return true;
  }

  return /^\/cars\/[^/]+$/.test(pathname);
};

const hasRecentDismissal = (): boolean => {
  try {
    const rawValue = localStorage.getItem(DISMISSED_AT_STORAGE_KEY);
    if (!rawValue) {
      return false;
    }

    const dismissedAt = Number(rawValue);
    if (!Number.isFinite(dismissedAt)) {
      return false;
    }

    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
};

const persistDismissal = (): void => {
  try {
    localStorage.setItem(DISMISSED_AT_STORAGE_KEY, String(Date.now()));
  } catch {
    // Non-blocking by design.
  }
};

const hasCookieConsent = (): boolean => {
  try {
    return localStorage.getItem(COOKIE_CONSENT_KEY) === "accepted";
  } catch {
    return false;
  }
};

export const useAppPrompt = (): UseAppPromptReturn => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const interactionTriggeredRef = useRef(false);
  const delayTimerRef = useRef<number | null>(null);

  const isRouteEligible = useMemo(() => isEligibleRoute(location.pathname), [location.pathname]);

  const clearDelayTimer = useCallback(() => {
    if (delayTimerRef.current === null) {
      return;
    }

    window.clearTimeout(delayTimerRef.current);
    delayTimerRef.current = null;
  }, []);

  const handleContinueOnWeb = useCallback(() => {
    persistDismissal();
    setIsModalOpen(false);
  }, []);

  const handleOpenApp = useCallback(async () => {
    persistDismissal();
    setIsModalOpen(false);
    navigate(WEB_BOOKING_PATH);
  }, [navigate]);

  useEffect(() => {
    if (!isAppPromptFeatureEnabled()) {
      return;
    }

    if (!isRouteEligible) {
      return;
    }

    const context = getMobileContext();
    if (!context.isMobileBrowser || context.isStandalone) {
      return;
    }

    if (hasRecentDismissal()) {
      return;
    }

    // Avoid stacking two bottom dialogs at once (cookie + app prompt).
    if (!hasCookieConsent()) {
      return;
    }

    let isMounted = true;

    const openModalIfEligible = (): void => {
      if (!isMounted || hasRecentDismissal()) {
        return;
      }

      setIsModalOpen(true);
    };

    const handleFirstInteraction = (): void => {
      if (interactionTriggeredRef.current) {
        return;
      }

      interactionTriggeredRef.current = true;
      openModalIfEligible();
    };

    window.addEventListener("scroll", handleFirstInteraction, { passive: true });
    window.addEventListener("click", handleFirstInteraction);
    window.addEventListener("touchstart", handleFirstInteraction, { passive: true });
    clearDelayTimer();
    delayTimerRef.current = window.setTimeout(openModalIfEligible, MODAL_DELAY_MS);

    return () => {
      isMounted = false;
      clearDelayTimer();
      window.removeEventListener("scroll", handleFirstInteraction);
      window.removeEventListener("click", handleFirstInteraction);
      window.removeEventListener("touchstart", handleFirstInteraction);
    };
  }, [clearDelayTimer, isRouteEligible]);

  useEffect(() => {
    interactionTriggeredRef.current = false;
    setIsModalOpen(false);
  }, [location.pathname]);

  return {
    isModalOpen,
    isOpeningApp: false,
    handleOpenApp,
    handleContinueOnWeb,
  };
};
